/* Rippl drift: a strip that drifts slowly and can be dragged, swiped or flicked (Apple audit round 2, C9, 10 Oct 2026).
   Source: theme-build/assets/rh-drift.js. Used by the home brand strip (rhm-brands) and the Hotels filmstrip (rhp-proof).

   Markup: a viewport marked data-rh-drift holding one or more tracks marked data-rh-drift-track. Each track's content
   is already doubled, so half its width is one loop (the same markup the old CSS marquee used). On a track:
   data-dir="-1" drifts the other way, data-speed is pixels a second (default 15). The script takes over from the
   track's CSS animation (it sets animation:none inline), so with the script missing the CSS marquee still runs.

   Behaviour:
   - The drift is time based (the same speed at 60 and 120 Hz) and runs only while the strip is on screen.
   - It pauses under a mouse, while anything inside has keyboard focus, and while the page is hidden.
   - Drag with a mouse or swipe with a finger: the strip follows the hand 1:1 from where it was grabbed. On release
     it carries on at the release speed and slows with Apple's scroll deceleration (0.998 a millisecond), then the
     drift eases back in 3 seconds later. A vertical swipe is left to the page (touch-action: pan-y; a horizontal
     move only takes over once it passes 10px and beats the vertical one).
   - Reduced motion: no drift; dragging still works.
   - A drag never fires a click on what was under the pointer. */
(function () {
  'use strict';
  if (window.RhDrift) return;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DECEL = 0.998, RESUME = 3000;

  function attach(vp) {
    if (!vp || vp.__rhd) return;
    var els = [].slice.call(vp.querySelectorAll('[data-rh-drift-track]'));
    if (!els.length) return;
    vp.__rhd = true;
    vp.style.touchAction = 'pan-y';
    vp.style.userSelect = vp.style.webkitUserSelect = 'none';
    vp.style.cursor = 'grab';
    var T = els.map(function (el) {
      el.style.animation = 'none';
      el.style.willChange = 'transform';
      return { el: el, x: 0, half: 0, dir: (+el.dataset.dir || 1) < 0 ? -1 : 1, speed: parseFloat(el.dataset.speed || vp.dataset.speed) || 15, v: 0, cur: 0 };
    });
    function measure() { T.forEach(function (t) { t.half = t.el.scrollWidth / 2 || t.el.offsetWidth / 2; wrap(t); paint(t); }); }
    function wrap(t) { if (!t.half) return; while (t.x > 0) t.x -= t.half; while (t.x <= -t.half) t.x += t.half; }
    function paint(t) { t.el.style.transform = 'translate3d(' + t.x.toFixed(2) + 'px,0,0)'; }
    T.forEach(function (t) { if (t.dir < 0) t.x = -1; });
    measure();
    if ('ResizeObserver' in window) { var ro = new ResizeObserver(measure); T.forEach(function (t) { ro.observe(t.el); }); }
    window.addEventListener('load', measure);

    var hover = false, focus = false, seen = true, held = null, resumeAt = 0, raf = 0, last = 0;
    function want(t, now) { return (!reduce.matches && !hover && !focus && !held && now >= resumeAt) ? t.speed : 0; }
    function step(now) {
      raf = 0;
      var dt = last ? Math.min(64, now - last) : 16; last = now;
      var busy = false;
      T.forEach(function (t) {
        if (held && held.t === t) return;
        /* momentum after a flick */
        if (Math.abs(t.v) > 0.005) { t.x += t.v * dt; t.v *= Math.pow(DECEL, dt); busy = true; } else t.v = 0;
        /* the drift eases in and out rather than jumping */
        var target = want(t, now);
        t.cur += (target - t.cur) * (1 - Math.exp(-dt / 1000 * 3));
        if (Math.abs(t.cur) < 0.01 && !target) t.cur = 0;
        if (t.cur) { t.x -= t.dir * t.cur * dt / 1000; busy = true; }
        wrap(t); paint(t);
      });
      if (seen && !document.hidden && (busy || held || !reduce.matches && !hover && !focus)) raf = requestAnimationFrame(step);
      else last = 0;
    }
    function wake() { if (!raf && seen && !document.hidden) { last = 0; raf = requestAnimationFrame(step); } }
    if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { es.forEach(function (e) { seen = e.isIntersecting; if (seen) wake(); }); }).observe(vp);
    document.addEventListener('visibilitychange', wake);
    if (reduce.addEventListener) reduce.addEventListener('change', wake);

    vp.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') { hover = true; } });
    vp.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') { hover = false; wake(); } });
    vp.addEventListener('focusin', function () { focus = true; });
    vp.addEventListener('focusout', function (e) { if (!e.relatedTarget || !vp.contains(e.relatedTarget)) { focus = false; wake(); } });

    /* ---- direct manipulation ---- */
    var moved = false;
    function trackAt(y) {
      var best = T[0], bd = Infinity;
      T.forEach(function (t) { var r = t.el.getBoundingClientRect(), d = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0; if (d < bd) { bd = d; best = t; } });
      return best;
    }
    vp.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      var t = trackAt(e.clientY);
      held = { t: t, id: e.pointerId, sx: e.clientX, sy: e.clientY, x0: t.x, on: e.pointerType === 'mouse', touch: e.pointerType !== 'mouse', hist: [[performance.now(), e.clientX]] };
      t.v = 0; t.cur = 0; moved = false;
      if (held.on) { try { vp.setPointerCapture(e.pointerId); } catch (x) {} vp.style.cursor = 'grabbing'; }
      wake();
    });
    vp.addEventListener('pointermove', function (e) {
      if (!held || e.pointerId !== held.id) return;
      var dx = e.clientX - held.sx, dy = e.clientY - held.sy;
      if (!held.on) {
        /* a finger: decide the direction once it has moved 10px; vertical belongs to the page */
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        if (Math.abs(dy) >= Math.abs(dx)) { release(e, true); return; }
        held.on = true;   /* no re-baseline: from here the strip sits exactly where 1:1 would put it */
        try { vp.setPointerCapture(e.pointerId); } catch (x) {}
      }
      if (Math.abs(dx) > 4) moved = true;
      var t = held.t; t.x = held.x0 + dx; wrap(t); paint(t);
      /* keep the grab point under the hand across the loop seam */
      held.x0 = t.x - dx;
      var now = performance.now(); held.hist.push([now, e.clientX]); while (held.hist.length > 2 && now - held.hist[0][0] > 100) held.hist.shift();
    });
    function release(e, cancel) {
      if (!held || (e && e.pointerId !== held.id)) return;
      var t = held.t, h = held.hist;
      if (!cancel && held.on && h.length > 1) {
        var a = h[0], b = h[h.length - 1], ms = b[0] - a[0];
        var age = performance.now() - b[0];
        t.v = (ms > 0 && age < 80) ? (b[1] - a[1]) / ms : 0;   /* px per ms */
        if (Math.abs(t.v) > 6) t.v = 6 * Math.sign(t.v);
      }
      try { vp.releasePointerCapture(held.id); } catch (x) {}
      held = null; vp.style.cursor = 'grab';
      resumeAt = performance.now() + RESUME;
      wake();
    }
    vp.addEventListener('pointerup', function (e) { release(e, false); });
    vp.addEventListener('pointercancel', function (e) { release(e, true); });
    /* only the strip's own capture ending counts: a finger is first captured implicitly by the image under it, and that
       capture ending (when the strip takes it over) must not end the drag */
    vp.addEventListener('lostpointercapture', function (e) { if (e.target === vp && held && held.on && e.pointerId === held.id) release(e, false); });
    vp.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
    vp.addEventListener('dragstart', function (e) { e.preventDefault(); });
    wake();
  }
  function boot() { [].slice.call(document.querySelectorAll('[data-rh-drift]')).forEach(attach); }
  window.RhDrift = { attach: attach, boot: boot };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
